/**
 * A workspace's sync history and putting a version back (git sync Stage 1,
 * card 7 set 7c; card 9 reads the same history filtered by one file).
 *
 * - loadGitHistory: the syncs, newest first, each with a summary from its file
 *   paths ("2 requests, 1 environment") — no file is read until one is opened.
 * - loadCommitItems: what one sync changed, as Apiboo items (changeItems.js).
 * - writeFileVersion: put one file's text (or its absence) into the mirror,
 *   recorded as Apiboo's own write so the file watcher does not echo it.
 */
import { invoke } from '@tauri-apps/api/core';
import { mirrorAbsDir } from '../workspaceMirror';
import { writeTextAbs } from '../gitFileWriter.js';
import { recordOwnDelete, recordOwnWrite } from '../gitOwnWrites.js';
import { describeChanges, groupOfPath, summarizeChanges } from './changeItems';

async function repoDir(workspace) {
  return (await mirrorAbsDir(workspace.id)).replace(/\\/g, '/');
}

/** @returns {Promise<Array<{oid, parent, time, message, author, files, summary}>>} */
export async function loadGitHistory(workspace, { path = null, limit = 100 } = {}) {
  if (!workspace?.git) return [];
  const commits = (await invoke('git_history', { repoPath: await repoDir(workspace), path, limit })) || [];
  return commits.map((c) => ({
    ...c,
    summary: summarizeChanges((c.files || []).map((f) => ({ group: groupOfPath(f.path) }))),
  }));
}

/** What one sync changed: before = the file at its parent, after = at the sync. */
export async function loadCommitItems(workspace, commit) {
  const dir = await repoDir(workspace);
  const at = (oid, path) => (oid ? invoke('git_file_at', { repoPath: dir, oid, path }) : Promise.resolve(null));
  // One page of a sync at a time (GitHistory reads 50): its files in parallel.
  const files = await Promise.all(
    (commit.files || []).map(async (f) => {
      const [before, after] = await Promise.all([f.kind === 'added' ? null : at(commit.parent, f.path), f.kind === 'deleted' ? null : at(commit.oid, f.path)]);
      return { ...f, before: before ?? null, after: after ?? null };
    })
  );
  return { items: describeChanges(files), files };
}

/** Write `content` to the mirror file at `path`, or delete it when `content` is null. */
export async function writeFileVersion(workspace, path, content) {
  if (!path || path.includes('..') || /^[\\/]|:/.test(path)) throw new Error(`Not a workspace file: ${path}`);
  const abs = `${await repoDir(workspace)}/${path}`;
  const fs = await import('@tauri-apps/plugin-fs');
  if (content == null) {
    recordOwnDelete(abs);
    try {
      await fs.remove(abs);
    } catch {
      /* already gone */
    }
    return;
  }
  recordOwnWrite(abs, content);
  await writeTextAbs(fs, abs, content);
}

/**
 * The text that "Discard" (back to the last sync) or "Restore this version"
 * (as it was after a sync) writes for one changed file. null = the file goes.
 */
export function versionFor(file, mode) {
  if (!file) return undefined;
  if (mode === 'discard') return file.kind === 'added' ? null : file.before;
  // restore: the file as that sync left it; a file that sync deleted comes back as it was before.
  return file.kind === 'deleted' ? file.before : file.after;
}

/** True when putting `path` back also changes other items (folders and settings share collection.yaml). */
export function sharesFile(path) {
  return /^collections\/[^/]+\/collection\.yaml$/.test(path);
}

const hasData = (snap) =>
  Boolean(
    snap &&
      ((snap.collections || []).length ||
        (snap.environments || []).length ||
        (snap.globalVariables || []).length ||
        (snap.workspaceVariables || []).length)
  );

/**
 * Discard / Restore one file (set 7c), in an order that cannot lose work:
 * the mirror is first brought up to date with the live app (so nothing newer
 * than the last flush is overwritten), the version is written, the mirror is
 * read back, and only a snapshot that reads cleanly — and is not suddenly
 * empty while the app has data — is handed to `apply`.
 *
 * @param {object} p
 * @param {object} p.workspace
 * @param {string|Array<{path: string, content: string|null}>} p.path  repo-relative file — or
 *   several, written together (a rename is two files, a moved folder more: renamePairs.js)
 * @param {string|null} p.content  the version (null = the file goes)
 * @param {object} p.live      the live snapshot (useWorkspaces.liveSnapshotFor)
 * @param {(ws, snap) => Promise<void>} p.refreshMirror
 * @param {(ws) => Promise<object>} p.readMirror
 * @param {(snap) => boolean} p.blocked  blockedByUnreadable
 * @param {(snap) => Promise<void>|void} p.apply
 */
export async function putBackVersion({ workspace, path, content, live, refreshMirror, readMirror, blocked, apply }) {
  await refreshMirror(workspace, live);
  const writes = Array.isArray(path) ? path : [{ path, content }];
  for (const w of writes) await writeFileVersion(workspace, w.path, w.content);
  const snap = await readMirror(workspace);
  if (!snap || blocked(snap)) {
    throw new Error('That version does not read back — nothing was changed in the app.');
  }
  if (!hasData(snap) && hasData(live)) {
    throw new Error('The repository folder read back empty — nothing was changed in the app.');
  }
  await apply(snap);
  return snap;
}
